import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { mlmAPI } from '../services/api';
import { Users, Network as NetworkIcon, ChevronDown, ChevronRight, UserCheck, Award, DollarSign, Search } from 'lucide-react';

const Network = () => {
  const [stats, setStats] = useState(null);
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statRes, treeRes] = await Promise.all([
          mlmAPI.getNetworkStats(),
          mlmAPI.getUnilevelTree(5),
        ]);
        setStats(statRes.data);
        setTree(treeRes.data);
      } catch (err) {
        console.error('Error loading network:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderTreeNode = (node, depth = 0) => {
    if (!node) return null;
    const isExpanded = expanded[node.id];
    const hasChildren = node.children && node.children.length > 0;
    const matchesSearch = search && (node.name?.toLowerCase().includes(search.toLowerCase()) || node.email?.toLowerCase().includes(search.toLowerCase()));

    if (search && !matchesSearch && !hasChildren) return null;

    return (
      <motion.div
        key={node.id}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: depth * 0.05 }}
      >
        <div
          className={`flex items-center gap-2 py-2 px-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer transition-colors ${
            matchesSearch ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700' : ''
          }`}
          style={{ paddingLeft: `${depth * 24 + 12}px` }}
          onClick={() => hasChildren && toggleExpand(node.id)}
        >
          {hasChildren ? (
            isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
          ) : (
            <div className="w-4 shrink-0" />
          )}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30 flex items-center justify-center text-sm font-bold text-orange-600 shrink-0">
            {node.name?.charAt(0) || '?'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{node.name || 'Unknown'}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{node.email || ''}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {node.rank && (
              <span className="text-xs bg-gradient-to-r from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 text-yellow-700 dark:text-yellow-300 px-2 py-0.5 rounded-full font-medium">
                {node.rank}
              </span>
            )}
            {node.vu !== undefined && (
              <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                VU: {node.vu}
              </span>
            )}
          </div>
        </div>
        {isExpanded && hasChildren && (
          <div className="ml-4 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
            {node.children.map((child) => renderTreeNode(child, depth + 1))}
          </div>
        )}
      </motion.div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }} className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
      </div>
    );
  }

  const statCards = [
    { label: 'Total Red', value: stats?.total_network || stats?.total_downline || 0, icon: Users, color: 'from-blue-500 to-cyan-500' },
    { label: 'Activos', value: stats?.active_members || 0, icon: UserCheck, color: 'from-green-500 to-emerald-500' },
    { label: 'Rango Actual', value: stats?.rank || '—', icon: Award, color: 'from-orange-500 to-amber-500' },
    { label: 'Vol. Total', value: `$${stats?.total_vu?.toFixed(2) || '0.00'}`, icon: DollarSign, color: 'from-purple-500 to-pink-500' },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Mi Red</h1>
        <p className="mt-1 text-gray-500 dark:text-gray-400">Visualiza y gestiona tu estructura de red</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map((card, i) => (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700"
          >
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-3`}>
              <card.icon className="w-5 h-5 text-white" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{card.value}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
        <div className="p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <NetworkIcon className="w-5 h-5 text-orange-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Árbol Unilevel</h2>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en la red..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-orange-500 focus:border-transparent w-64"
              />
            </div>
          </div>
        </div>
        <div className="p-4 max-h-[600px] overflow-y-auto">
          {tree ? renderTreeNode(tree) : (
            <p className="text-center text-gray-500 py-8">No hay datos de red disponibles</p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default Network;
